import React, {useCallback, useState} from 'react';
import {modelMetadataUpdateOption} from '../../../../../../store/modelMetadata';
import {useDispatch} from 'react-redux';
import {messageAdd, MessageTypes} from '../../../../../../store/message';
import {useMounted} from '../../../../../../hooks/useMounted';
import {trackModelMetadataOptionRetired} from '../../../../../../tracking/tracking';
import UsageCountTitle from '../updateOption/UsageCountTitle';
import UsageList from '../updateOption/UsageList';
import {ManageMetadataActionProps} from '../ActionMenuTypes';
import {tid} from '../../../../../../testUtils';
import {MANAGE_METADATA_MENU_IDS} from '../../../../../../test/types';
import {CloseOutlined, PoweroffOutlined} from '@ant-design/icons';
import {Checkbox, Button} from 'antd';
import styles from './RetireOption.module.scss';

const RetireOption: React.FC<ManageMetadataActionProps> = ({
  property,
  value,
  onSuccess,
  onCancel,
  refreshOptionsCount,
  fileTagCount,
}) => {
  const dispatch = useDispatch();
  const [agree, setAgree] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const mounted = useMounted();

  const handleSubmit = useCallback(() => {
    if (!agree) return;
    setSubmitting(true);
    return modelMetadataUpdateOption(dispatch, property, value, value, undefined, true)
      .then(() => {
        dispatch(messageAdd(`The option has been retired`, MessageTypes.SUCCESS));
        trackModelMetadataOptionRetired();
        onSuccess();
        if (refreshOptionsCount) refreshOptionsCount();
      })
      .catch(() => null)
      .finally(() => {
        mounted.current && setSubmitting(false);
      });
  }, [dispatch, onSuccess, property, value, mounted, refreshOptionsCount, agree]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h2 className={styles.title}>Retire Option</h2>
        </div>
        <button className={styles.closeBtn} onClick={onCancel} aria-label="Close">
          <CloseOutlined />
        </button>
      </div>
      <div className={styles.formWrapper}>
        <p className={styles.subtitle}>
          <UsageCountTitle property={property} value={value} fileTagCount={fileTagCount} />
        </p>

        <div className={styles.confirmSection}>
          <Checkbox
            checked={agree}
            onChange={(e) => setAgree(e.target.checked)}
            disabled={submitting}
            className={styles.confirmCheckbox}
            {...tid(MANAGE_METADATA_MENU_IDS.RETIRE, 'checkbox')}
          >
            I confirm that this option will be permanently retired.
          </Checkbox>
        </div>

        <div className={styles.buttonsContainer}>
          <Button
            onClick={onCancel}
            disabled={submitting}
            className={styles.cancelBtn}
            {...tid(MANAGE_METADATA_MENU_IDS.RETIRE, 'cancel')}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleSubmit}
            loading={submitting}
            disabled={!agree || submitting}
            icon={<PoweroffOutlined />}
            className={styles.submitBtn}
            {...tid(MANAGE_METADATA_MENU_IDS.RETIRE, 'submit')}
          >
            Retire Option
          </Button>
        </div>

        <UsageList property={property} value={value} fileTagCount={fileTagCount} />
      </div>
    </div>
  );
};

export default RetireOption;
